import { G } from '@mobily/ts-belt';
import Description from '@mui/icons-material/Description';
import { Box, Card, Link, Table, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useMemo } from 'react';
import {
  ChipField,
  FileField,
  Labeled,
  required,
  useGetMany,
  useRecordContext,
  useTranslate,
} from 'react-admin';

import { GetB2bCampaignOneResponse } from '@Common/types/bo/b2b/campaigns';
import { B2bContract } from '@Common/types/bo/b2b/contracts';
import { getCampaignAgreementCashSource } from '@Common/validators/contracts';

import { FileInput, ShowSection } from '@sh4res/bo-common/components';
import { DownloadOperationsFileSample } from '@sh4res/bo-common/components/B2bCampaigns/DownloadSample';
import ExampleFilePath from '@sh4res/bo-common/components/B2bCampaigns/steps/affiliates_data/distribution_rule_file_sample.xlsm';
import ExampleMlkFilePath from '@sh4res/bo-common/components/B2bCampaigns/steps/affiliates_data/distribution_rule_file_sample_mlk.xlsm';
import { B2bCampaignFormValues } from '@sh4res/bo-common/components/B2bCampaigns/types';
import { useResourceBasePath, useWatchObject } from '@sh4res/bo-common/hooks';
import { getBrandedFile } from '@sh4res/bo-common/theme';

const testId = 'BoExternalB2bCampaignAffiliatesDataStep';

export const AffiliatesDataStep = ({
  isBoExternal,
  distributorStepsOverride = false,
}: {
  isBoExternal: boolean;
  distributorStepsOverride?: boolean;
}) => {
  const record = useRecordContext<GetB2bCampaignOneResponse>();
  const resourceBasePath = useResourceBasePath();
  const exampleFilePath = getBrandedFile(ExampleFilePath, ExampleMlkFilePath);

  const translate = useTranslate();
  const {
    company_id: companyId,
    type,
    b2b_contract_ids: contractIds,
    distribution_rule_file_source: distributionRuleFile,
  } = useWatchObject<B2bCampaignFormValues>()([
    'company_id',
    'type',
    'b2b_contract_ids',
    'distribution_rule_file_source',
  ]);

  const editionDisabled = useMemo(() => {
    if (isBoExternal || distributorStepsOverride) {
      // enabled on creation or on draft
      return !!record && record.status !== 'campaign_draft';
    }

    // Always disabled in internal BO
    return true;
  }, [record, isBoExternal, distributorStepsOverride]);

  const { data: contracts } = useGetMany<B2bContract>(
    `${resourceBasePath}contracts`,
    {
      ids: contractIds,
    },
    { enabled: Array.isArray(contractIds) && contractIds.length > 0 },
  );
  const agreementKey = getCampaignAgreementCashSource(type);
  // Show first contract holding the agreement matching the campaign type
  const agreement = agreementKey
    ? contracts?.find((entry) => G.isNotNullable(entry[agreementKey]))?.[agreementKey]
    : null;
  return (
    <Box>
      <ShowSection
        leftAligned
        label="resources.bo_common.b2b.campaigns.sections.affiliates.title"
        noCard={true}>
        <Typography>
          {translate('resources.bo_common.b2b.campaigns.sections.affiliates.description')}
        </Typography>
      </ShowSection>
      <ShowSection
        leftAligned
        label="resources.bo_common.b2b.campaigns.sections.affiliates.distribution_title"
        labelParams={{ type: translate(`resources.bo_common.b2b.campaigns.choices.type.${type}`) }}
        noCard={true}>
        <Card>
          <Table>
            <TableHead>
              <TableRow sx={{ fontWeight: 'bold' }}>
                <TableCell>
                  {translate('resources.bo_common.b2b.campaigns.sections.affiliates.rule')}
                </TableCell>
                <TableCell>%</TableCell>
              </TableRow>
            </TableHead>
            <TableRow>
              <TableCell>
                {translate(
                  'resources.bo_common.b2b.campaigns.sections.affiliates.distribution_salary',
                )}
              </TableCell>
              <TableCell>
                <Typography>{agreement?.distribution_salary_pct ?? 0}%</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                {translate(
                  'resources.bo_common.b2b.campaigns.sections.affiliates.distribution_tenure',
                )}
              </TableCell>
              <TableCell>
                <Typography>{agreement?.distribution_tenure_pct ?? 0}%</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                {translate(
                  'resources.bo_common.b2b.campaigns.sections.affiliates.distribution_uniformly',
                )}
              </TableCell>
              <TableCell>
                {/* TODO: add these rules to other campaign types https://sh4res.atlassian.net/browse/BRAV-7320*/}
                <Typography>{agreement?.distribution_uniformity_pct ?? 0}%</Typography>
              </TableCell>
            </TableRow>
            <TableRow sx={{ fontWeight: 'bold' }}>
              <TableCell>
                {translate(
                  'resources.bo_common.b2b.campaigns.sections.affiliates.distribution_total',
                )}
              </TableCell>
              <TableCell>
                <Typography>
                  {(agreement?.distribution_tenure_pct ?? 0) +
                    (agreement?.distribution_salary_pct ?? 0) +
                    (agreement?.distribution_uniformity_pct ?? 0)}
                  %
                </Typography>
              </TableCell>
            </TableRow>
          </Table>
        </Card>
      </ShowSection>
      <ShowSection
        noCard
        leftAligned
        label="resources.bo_common.b2b.campaigns.sections.affiliates.distribution_file">
        <Typography>
          {translate(
            'resources.bo_common.b2b.campaigns.sections.affiliates.distribution_file_desc',
          )}
        </Typography>
        <Link
          sx={{ display: 'flex', gap: 1 }}
          underline="hover"
          href={exampleFilePath}
          download={translate(
            'resources.bo_common.b2b.campaigns.sections.allocations.download_sample_all_employees_file',
          )}>
          <Description />
          <span>
            {translate(
              'resources.bo_common.b2b.campaigns.sections.allocations.download_sample_empty',
            )}
          </span>
        </Link>

        {G.isNotNullable(companyId) && (
          <>
            <DownloadOperationsFileSample
              body={{
                kind: 'campaign_sample',
                variant: 'distribution_rule',
                company_id: companyId,
                contract_ids: contractIds,
                employees: 'current',
              }}
              label="resources.bo_common.b2b.campaigns.sections.allocations.download_sample_current_employees"
            />
            <DownloadOperationsFileSample
              body={{
                kind: 'campaign_sample',
                variant: 'distribution_rule',
                company_id: companyId,
                contract_ids: contractIds,
                employees: 'all',
              }}
              label="resources.bo_common.b2b.campaigns.sections.allocations.download_sample_all_employees"
            />
          </>
        )}
        {!editionDisabled && (
          <FileInput
            data-testid={`${testId}_DistributionRuleFileInput`}
            source="distribution_rule_file_source"
            accept=".xlsx,.xlsm"
            isRequired={!record?.distribution_rule_file_url}
            validate={record?.distribution_rule_file_url ? [] : [required()]}>
            <ChipField data-testid={`${testId}_DistributionRuleFileTitleField`} source="title" />
          </FileInput>
        )}
        {!distributionRuleFile && record?.distribution_rule_file_url && (
          <Labeled label="resources.bo_common.b2b.campaigns.sections.affiliates.distribution_file_url">
            <FileField
              data-testid={`${testId}_DistributionRuleFileField`}
              source="distribution_rule_file_url"
              record={{
                ...record,
                distribution_rule_file_tile: decodeURIComponent(
                  record.distribution_rule_file_url.split('/').pop()?.split('?')[0] || '',
                ),
              }}
              target="_blank"
              title="distribution_rule_file_tile"
            />
          </Labeled>
        )}
      </ShowSection>
    </Box>
  );
};
